import { useMemo } from 'react'

import { ClipboardIcon, ShoppingCartAdd02Icon, StarIcon } from '@hugeicons/react'

import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { useRewardsStore } from '@/shared/store/useRewardsStore'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import type { PrizeRule } from '@/types/prizes'
import { PrizeRulesKindsEnum } from '@/types/prizes'

import { buildStoreItemSheetHref } from '../../store/lib/storeSheetSearch'
import { PrizeSheetSection } from '../components/PrizeSheetSection'
import { getPrizeRuleMessage } from '../lib/prizeRuleMessage'

type PrizePartner = { name?: string } | null | undefined

type RulesProps = {
  prizeRules?: PrizeRule[]
  isParticipating?: boolean
  prizePartner?: PrizePartner
}

type RulesItemProps = PrizeRule & {
  isParticipating?: boolean
  prizePartner?: PrizePartner
}

const RulesItem = ({
  prize_rulable,
  kind,
  isParticipating,
  prizePartner
}: RulesItemProps) => {
  const { rewardItems } = useRewardsStore()
  const router = useAppRouter()

  const rewardItem = useMemo(
    () => rewardItems?.find(item => item.id == prize_rulable?.id),
    [prize_rulable?.id, rewardItems]
  )

  const handleShowStoreItem = () => {
    if (!rewardItem?.reward?.id) return

    router.push(
      buildStoreItemSheetHref(rewardItem.reward.id, rewardItem.id)
    )
  }

  const shouldShowItemStoreButton =
    kind === PrizeRulesKindsEnum.VOUCHER_BUYERS && !isParticipating

  return (
    <Card className="shadow-sm">
      <Card.Body className="flex items-center gap-3 p-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-surface text-primary">
          <StarIcon size={16} variant="duotone" aria-hidden />
        </span>
        <span className="min-w-0 flex-1 text-sm font-semibold leading-snug text-gray-800">
          {getPrizeRuleMessage(prize_rulable, kind, prizePartner)}
        </span>
        {shouldShowItemStoreButton && (
          <Button
            variant="primary"
            size="sm"
            icon
            aria-label="Abrir lojinha"
            leadingIcon={<ShoppingCartAdd02Icon size={14} variant="stroke" />}
            onClick={handleShowStoreItem}
          />
        )}
      </Card.Body>
    </Card>
  )
}

export const DigitalProductPrizeParticipationRules = ({
  prizeRules,
  isParticipating,
  prizePartner
}: RulesProps) => {
  if (!prizeRules?.length) return null

  return (
    <PrizeSheetSection
      title="Regras de participação"
      icon={<ClipboardIcon size={16} variant="duotone" aria-hidden />}
      className="mb-5"
    >
      <div className="flex flex-col gap-2">
        {prizeRules.map((rule, index) => (
          <RulesItem
            key={rule.id ?? index}
            {...rule}
            isParticipating={isParticipating}
            prizePartner={prizePartner}
          />
        ))}
      </div>
    </PrizeSheetSection>
  )
}
